/**
 * Hook React Query pour le profil utilisateur complet
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { profileClient } from '../api';
import type { CompleteUserProfile } from '@sphere/user/domain';

/** Clés de cache pour le profil */
export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
  user: (userId: string) => [...profileKeys.all, 'user', userId] as const,
};

/**
 * Hook pour récupérer le profil complet de l'utilisateur connecté
 */
export function useMyCompleteProfile(
  options?: Omit<
    UseQueryOptions<CompleteUserProfile, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: () => profileClient.getMyProfile(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook pour récupérer le profil complet d'un utilisateur spécifique
 */
export function useUserCompleteProfile(
  userId: string | undefined,
  options?: Omit<
    UseQueryOptions<CompleteUserProfile, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: profileKeys.user(userId || ''),
    queryFn: () => {
      if (!userId) {
        throw new Error('userId is required');
      }
      return profileClient.getUserProfile(userId);
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
}
